import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save, RotateCcw, AlertCircle, CheckCircle, Database, Clock,
  Globe, Bell, Settings as SettingsIcon, Monitor, Plus, Minus,
  Sun, Moon, BarChart, LineChart, TrendingUp, Calendar, X, Mail, Phone,
  CheckSquare, Square
} from 'lucide-react';
import { Card, Button, Input, Toggle, Modal } from '../components/ui';
import { useToast } from '../hooks/use-toast';
import { MOCK_USERS } from '../utils/mockUsersData';
import { BRAND } from '../config/brand';
import { STORAGE_KEYS, readStorageValue, writeStorageValue } from '../utils/storageKeys';
import './SettingsPage.css';

// Default settings
const DEFAULT_SETTINGS = {
  general: {
    companyName: BRAND.defaultCompanyName,
    farmName: '',
    language: 'en',
    timezone: 'Europe/Rome',
    timeFormat: '24h',
    dateFormat: 'DD/MM/YYYY',
  },
  notifications: {
    emailEnabled: true,
    smsEnabled: false,
    emailRecipients: [], // Array of user IDs
    emailCustomAddresses: [], // Array of custom email strings
    smsRecipients: [], // Array of user IDs
    smsCustomNumbers: [], // Array of custom phone number strings
    thresholds: {
      temperatureMin: 15,
      temperatureMax: 40,
      humidityMin: 30,
      humidityMax: 90,
      irradiationMin: 0,
      irradiationMax: 1200,
      batteryMin: 20,
    },
    progressReportFrequency: 'monthly',
  },
  sensors: {
    updateInterval: 30,
    dataRetention: 180,
    autoRefresh: true,
    refreshInterval: 30,
  },
  display: {
    theme: 'light',
    chartType: 'line',
    layout: 'grid',
    cardsPerRow: 3,
  },
};

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const resolvedLang = i18n.resolvedLanguage || i18n.language || 'en';
  const locale = resolvedLang.startsWith('it')
    ? 'it-IT'
    : resolvedLang.startsWith('es')
      ? 'es-ES'
      : 'en-US';
  const lastBackupText = new Date('2024-11-14T02:30:00').toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const systemUptimeText = `${15} ${t('time.days')}, ${3} ${t('time.hours')}`;
  
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState(DEFAULT_SETTINGS);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [customPhoneInput, setCustomPhoneInput] = useState('');

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = readStorageValue(window.localStorage, STORAGE_KEYS.settings);
    if (saved?.value) {
      try {
        const parsed = JSON.parse(saved.value);
        setSettings(parsed);
        setOriginalSettings(parsed);
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  // Check if form is dirty
  useEffect(() => {
    const dirty = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setIsDirty(dirty);
  }, [settings, originalSettings]);

  // Update settings helper
  const updateSetting = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Update nested settings helper
  const updateNestedSetting = (section, subsection, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value,
        },
      },
    }));
  };

  // Helper to toggle user in recipients array
  const toggleUserRecipient = (type, userId) => {
    const field = type === 'email' ? 'emailRecipients' : 'smsRecipients';
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: prev.notifications[field].includes(userId)
          ? prev.notifications[field].filter(id => id !== userId)
          : [...prev.notifications[field], userId],
      },
    }));
  };

  // Helper to select/deselect all users
  const toggleAllUsers = (type) => {
    const field = type === 'email' ? 'emailRecipients' : 'smsRecipients';
    const currentRecipients = settings.notifications[field];
    const allSelected = currentRecipients.length === activeUsers.length;
    
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: allSelected ? [] : activeUsers.map(user => user.id),
      },
    }));
  };

  // Check if all users are selected
  const areAllUsersSelected = (type) => {
    const field = type === 'email' ? 'emailRecipients' : 'smsRecipients';
    return settings.notifications[field].length === activeUsers.length && activeUsers.length > 0;
  };

  // Get selected count
  const getSelectedCount = (type) => {
    const field = type === 'email' ? 'emailRecipients' : 'smsRecipients';
    return settings.notifications[field].length;
  };

  // Helper to add custom contact
  const addCustomContact = (type, value) => {
    if (!value || !value.trim()) return;
    
    const field = type === 'email' ? 'emailCustomAddresses' : 'smsCustomNumbers';
    const trimmedValue = value.trim();
    
    // Check for duplicates
    const existing = type === 'email' 
      ? settings.notifications.emailCustomAddresses 
      : settings.notifications.smsCustomNumbers;
    
    if (existing.includes(trimmedValue)) {
      toast({
        title: t('settings.notifications.duplicateEntryTitle'),
        description: type === 'email'
          ? t('settings.notifications.duplicateEmail')
          : t('settings.notifications.duplicatePhone'),
        variant: 'destructive',
      });
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: [...prev.notifications[field], trimmedValue],
      },
    }));
    
    // Clear input
    if (type === 'email') {
      setCustomEmailInput('');
    } else {
      setCustomPhoneInput('');
    }
  };

  // Helper to remove custom contact
  const removeCustomContact = (type, index) => {
    const field = type === 'email' ? 'emailCustomAddresses' : 'smsCustomNumbers';
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: prev.notifications[field].filter((_, i) => i !== index),
      },
    }));
  };

  // Get active users only
  const activeUsers = MOCK_USERS.filter(user => user.isActive);

  // Validation
  const validate = () => {
    const newErrors = {};

    // Temperature validation
    if (settings.notifications.thresholds.temperatureMin >= settings.notifications.thresholds.temperatureMax) {
      newErrors.temperatureMin = t('settings.validation.temperatureMinLessThanMax');
    }

    // Humidity validation
    if (settings.notifications.thresholds.humidityMin >= settings.notifications.thresholds.humidityMax) {
      newErrors.humidityMin = t('settings.validation.humidityMinLessThanMax');
    }

    // Irradiation validation
    if (settings.notifications.thresholds.irradiationMin >= settings.notifications.thresholds.irradiationMax) {
      newErrors.irradiationMin = t('settings.validation.irradiationMinLessThanMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save settings
  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: t('common.error'),
        description: t('settings.validationError'),
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      writeStorageValue(window.localStorage, STORAGE_KEYS.settings, JSON.stringify(settings));
      setOriginalSettings(settings);
      setIsDirty(false);
      
      toast({
        title: t('common.success'),
        description: t('settings.savedSuccess'),
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('settings.saveFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setShowResetModal(false);
    toast({
      title: t('common.success'),
      description: t('settings.resetSuccess'),
      variant: 'default',
    });
  };

  return (
    <div className="settings-page">
      {/* Page Header */}
      <div className="settings-header">
        <div className="settings-actions">
          <Button
            variant="secondary"
            icon={<RotateCcw size={18} />}
            onClick={() => setShowResetModal(true)}
          >
            {t('settings.resetToDefaults')}
          </Button>
          <Button
            variant="primary"
            icon={<Save size={18} />}
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? t('settings.saving') : t('settings.saveChanges')}
            {isDirty && <span className="unsaved-indicator" />}
          </Button>
        </div>
      </div>

      {/* Settings Container */}
      <div className="settings-container">
        {/* 1. General Settings */}
        <Card className="settings-card" padding="lg">
          <div className="settings-card-header">
            <div>
              <h2 className="settings-card-title">
                <Globe size={20} />
                {t('settings.general.title')}
              </h2>
              <p className="settings-card-description">
                {t('settings.general.description')}
              </p>
            </div>
          </div>

          <div className="settings-form">
            {/* Company Name */}
            <div className="form-field">
              <label className="form-label">{t('settings.general.companyName')}</label>
              <Input
                value={settings.general.companyName}
                onChange={(value) => updateSetting('general', 'companyName', value)}
                placeholder={t('settings.general.companyNamePlaceholder')}
              />
              <p className="form-helper">{t('settings.general.companyNameHelper')}</p>
            </div>

            {/* Farm Name */}
            <div className="form-field">
              <label className="form-label">{t('settings.general.farmName')}</label>
              <Input
                value={settings.general.farmName}
                onChange={(value) => updateSetting('general', 'farmName', value)}
                placeholder={t('settings.general.farmNamePlaceholder')}
              />
              <p className="form-helper">{t('settings.general.farmNameHelper')}</p>
            </div>

            {/* Language */}
            <div className="form-field">
              <label className="form-label">{t('settings.general.language')}</label>
              <select
                className="form-select"
                value={settings.general.language}
                onChange={(e) => updateSetting('general', 'language', e.target.value)}
              >
                <option value="en">{t('settings.general.languages.en')}</option>
                <option value="it">{t('settings.general.languages.it')}</option>
                <option value="es">{t('settings.general.languages.es')}</option>
              </select>
            </div>

            {/* Time Format */}
            <div className="form-field">
              <label className="form-label">{t('settings.general.timeFormat')}</label>
              <div className="form-radio-group">
                <button
                  className={`form-radio-option ${settings.general.timeFormat === '12h' ? 'form-radio-option--selected' : ''}`}
                  onClick={() => updateSetting('general', 'timeFormat', '12h')}
                >
                  <span className="form-radio-button">
                    {settings.general.timeFormat === '12h' && <span className="form-radio-dot" />}
                  </span>
                  {t('settings.general.timeFormat12h')}
                </button>
                <button
                  className={`form-radio-option ${settings.general.timeFormat === '24h' ? 'form-radio-option--selected' : ''}`}
                  onClick={() => updateSetting('general', 'timeFormat', '24h')}
                >
                  <span className="form-radio-button">
                    {settings.general.timeFormat === '24h' && <span className="form-radio-dot" />}
                  </span>
                  {t('settings.general.timeFormat24h')}
                </button>
              </div>
            </div>

            {/* Date Format */}
            <div className="form-field">
              <label className="form-label">{t('settings.general.dateFormat')}</label>
              <select
                className="form-select"
                value={settings.general.dateFormat}
                onChange={(e) => updateSetting('general', 'dateFormat', e.target.value)}
              >
                <option value="DD/MM/YYYY">{t('settings.general.dateFormats.european')}</option>
                <option value="MM/DD/YYYY">{t('settings.general.dateFormats.us')}</option>
                <option value="YYYY-MM-DD">{t('settings.general.dateFormats.iso')}</option>
              </select>
              <p className="form-helper">{t('settings.general.dateFormatExample')}</p>
            </div>
          </div>
        </Card>

        {/* 2. Notification Settings */}
        <Card className="settings-card" padding="lg">
          <div className="settings-card-header">
            <div>
              <h2 className="settings-card-title">
                <Bell size={20} />
                {t('settings.notifications.title')}
              </h2>
              <p className="settings-card-description">
                {t('settings.notifications.subtitle')}
              </p>
            </div>
          </div>

          <div className="settings-form">
            {/* Email Toggle */}
            <div className="form-toggle-row">
              <div>
                <div className="form-label">{t('settings.notifications.emailEnabled')}</div>
                <p className="form-helper">{t('settings.notifications.emailEnabledHelper')}</p>
              </div>
              <Toggle
                checked={settings.notifications.emailEnabled}
                onChange={(checked) => updateSetting('notifications', 'emailEnabled', checked)}
              />
            </div>

            {/* Email Recipients */}
            {settings.notifications.emailEnabled && (
              <div className="form-field form-recipients-section">
                <div className="form-recipients-header">
                  <label className="form-label">
                    <Mail size={16} />
                    {t('settings.notifications.emailRecipients')}
                    <span className="form-label-count">
                      {t('settings.notifications.selectedOfTotal', {
                        selected: getSelectedCount('email'),
                        total: activeUsers.length,
                      })}
                    </span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllUsers('email')}
                    className="form-select-all-btn"
                  >
                    {areAllUsersSelected('email') ? (
                      <>
                        <CheckSquare size={16} />
                        {t('common.deselectAll')}
                      </>
                    ) : (
                      <>
                        <Square size={16} />
                        {t('common.selectAll')}
                      </>
                    )}
                  </Button>
                </div>
                <div className="form-user-list-container">
                  <div className="form-user-list">
                    {activeUsers.map(user => (
                      <label key={user.id} className="form-checkbox-item">
                        <input
                          type="checkbox"
                          checked={settings.notifications.emailRecipients.includes(user.id)}
                          onChange={() => toggleUserRecipient('email', user.id)}
                          className="form-checkbox"
                        />
                        <span className="form-checkbox-label">
                          <span className="form-checkbox-name">{user.firstName} {user.lastName}</span>
                          <span className="form-checkbox-email">{user.email}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-custom-contacts-section">
                  <label className="form-sublabel">{t('settings.notifications.customEmail')}</label>
                  <div className="form-custom-contacts-input">
                    <input
                      type="email"
                      className="form-input"
                      placeholder={t('settings.notifications.enterEmail')}
                      value={customEmailInput}
                      onChange={(e) => setCustomEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomContact('email', customEmailInput);
                        }
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => addCustomContact('email', customEmailInput)}
                      disabled={!customEmailInput.trim()}
                    >
                      <Plus size={16} />
                      {t('common.add')}
                    </Button>
                  </div>
                  {settings.notifications.emailCustomAddresses.length > 0 && (
                    <div className="form-custom-contacts-list">
                      {settings.notifications.emailCustomAddresses.map((email, index) => (
                        <div key={index} className="form-custom-contact-tag">
                          <Mail size={12} />
                          <span>{email}</span>
                          <button
                            type="button"
                            className="form-custom-contact-remove"
                            onClick={() => removeCustomContact('email', index)}
                            aria-label={t('settings.notifications.removeEmail')}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SMS Toggle */}
            <div className="form-toggle-row">
              <div>
                <div className="form-label">{t('settings.notifications.smsEnabled')}</div>
                <p className="form-helper">{t('settings.notifications.smsEnabledHelper')}</p>
              </div>
              <Toggle
                checked={settings.notifications.smsEnabled}
                onChange={(checked) => updateSetting('notifications', 'smsEnabled', checked)}
              />
            </div>

            {/* SMS Recipients */}
            {settings.notifications.smsEnabled && (
              <div className="form-field form-recipients-section">
                <div className="form-recipients-header">
                  <label className="form-label">
                    <Phone size={16} />
                    {t('settings.notifications.smsRecipients')}
                    <span className="form-label-count">
                      {t('settings.notifications.selectedOfTotal', {
                        selected: getSelectedCount('sms'),
                        total: activeUsers.length,
                      })}
                    </span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllUsers('sms')}
                    className="form-select-all-btn"
                  >
                    {areAllUsersSelected('sms') ? (
                      <>
                        <CheckSquare size={16} />
                        {t('common.deselectAll')}
                      </>
                    ) : (
                      <>
                        <Square size={16} />
                        {t('common.selectAll')}
                      </>
                    )}
                  </Button>
                </div>
                <div className="form-user-list-container">
                  <div className="form-user-list">
                    {activeUsers.map(user => (
                      <label key={user.id} className="form-checkbox-item">
                        <input
                          type="checkbox"
                          checked={settings.notifications.smsRecipients.includes(user.id)}
                          onChange={() => toggleUserRecipient('sms', user.id)}
                          className="form-checkbox"
                        />
                        <span className="form-checkbox-label">
                          <span className="form-checkbox-name">{user.firstName} {user.lastName}</span>
                          <span className="form-checkbox-email">{user.phone}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-custom-contacts-section">
                  <label className="form-sublabel">{t('settings.notifications.customPhone')}</label>
                  <div className="form-custom-contacts-input">
                    <input
                      type="tel"
                      className="form-input"
                      placeholder={t('settings.notifications.enterPhone')}
                      value={customPhoneInput}
                      onChange={(e) => setCustomPhoneInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomContact('sms', customPhoneInput);
                        }
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => addCustomContact('sms', customPhoneInput)}
                      disabled={!customPhoneInput.trim()}
                    >
                      <Plus size={16} />
                      {t('common.add')}
                    </Button>
                  </div>
                  {settings.notifications.smsCustomNumbers.length > 0 && (
                    <div className="form-custom-contacts-list">
                      {settings.notifications.smsCustomNumbers.map((phone, index) => (
                        <div key={index} className="form-custom-contact-tag">
                          <Phone size={12} />
                          <span>{phone}</span>
                          <button
                            type="button"
                            className="form-custom-contact-remove"
                            onClick={() => removeCustomContact('sms', index)}
                            aria-label={t('settings.notifications.removePhone')}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alert Thresholds */}
            <div className="form-section-divider">
              <h3 className="form-section-title">{t('settings.notifications.thresholds.title')}</h3>
            </div>

            {/* Temperature Range */}
            <div className="form-field">
              <label className="form-label">{t('settings.notifications.thresholds.temperature')}</label>
              <div className="form-range-inputs">
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.min')}:</span>
                  <input
                    type="number"
                    className={`form-input form-input--small ${errors.temperatureMin ? 'form-input--error' : ''}`}
                    value={settings.notifications.thresholds.temperatureMin}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'temperatureMin', Number(e.target.value))}
                  />
                  <span className="form-range-suffix">°C</span>
                </div>
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.max')}:</span>
                  <input
                    type="number"
                    className="form-input form-input--small"
                    value={settings.notifications.thresholds.temperatureMax}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'temperatureMax', Number(e.target.value))}
                  />
                  <span className="form-range-suffix">°C</span>
                </div>
              </div>
              {errors.temperatureMin && (
                <p className="form-error">
                  <AlertCircle size={14} />
                  {errors.temperatureMin}
                </p>
              )}
              <p className="form-helper">{t('settings.notifications.thresholds.temperatureHelper')}</p>
            </div>

            {/* Humidity Range */}
            <div className="form-field">
              <label className="form-label">{t('settings.notifications.thresholds.humidity')}</label>
              <div className="form-range-inputs">
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.min')}:</span>
                  <input
                    type="number"
                    className={`form-input form-input--small ${errors.humidityMin ? 'form-input--error' : ''}`}
                    value={settings.notifications.thresholds.humidityMin}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'humidityMin', Number(e.target.value))}
                  />
                  <span className="form-range-suffix">%</span>
                </div>
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.max')}:</span>
                  <input
                    type="number"
                    className="form-input form-input--small"
                    value={settings.notifications.thresholds.humidityMax}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'humidityMax', Number(e.target.value))}
                  />
                  <span className="form-range-suffix">%</span>
                </div>
              </div>
              {errors.humidityMin && (
                <p className="form-error">
                  <AlertCircle size={14} />
                  {errors.humidityMin}
                </p>
              )}
            </div>

            {/* Irradiation Range */}
            <div className="form-field">
              <label className="form-label">{t('settings.notifications.thresholds.irradiation')}</label>
              <div className="form-range-inputs">
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.min')}:</span>
                  <input
                    type="number"
                    className={`form-input form-input--small ${errors.irradiationMin ? 'form-input--error' : ''}`}
                    value={settings.notifications.thresholds.irradiationMin}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'irradiationMin', Number(e.target.value))}
                    min="0"
                  />
                  <span className="form-range-suffix">W/m²</span>
                </div>
                <div className="form-range-input">
                  <span className="form-range-prefix">{t('settings.notifications.thresholds.max')}:</span>
                  <input
                    type="number"
                    className="form-input form-input--small"
                    value={settings.notifications.thresholds.irradiationMax}
                    onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'irradiationMax', Number(e.target.value))}
                    min="0"
                  />
                  <span className="form-range-suffix">W/m²</span>
                </div>
              </div>
              {errors.irradiationMin && (
                <p className="form-error">
                  <AlertCircle size={14} />
                  {errors.irradiationMin}
                </p>
              )}
              <p className="form-helper">{t('settings.notifications.thresholds.irradiationHelper')}</p>
            </div>

            {/* Battery Alert */}
            <div className="form-field">
              <label className="form-label">{t('settings.notifications.thresholds.battery')}</label>
              <div className="form-range-input">
                <span className="form-range-prefix">{t('settings.notifications.thresholds.alertBelow')}:</span>
                <input
                  type="number"
                  className="form-input form-input--small"
                  value={settings.notifications.thresholds.batteryMin}
                  onChange={(e) => updateNestedSetting('notifications', 'thresholds', 'batteryMin', Number(e.target.value))}
                  min="0"
                  max="100"
                />
                <span className="form-range-suffix">%</span>
              </div>
            </div>

            {/* Progress Report Frequency */}
            <div className="form-field">
              <label className="form-label">{t('settings.notifications.progressReports.frequency')}</label>
              <p className="form-helper" style={{ marginBottom: 'var(--spacing-3)' }}>
                {t('settings.notifications.progressReports.subtitle')}
              </p>
              <div className="form-radio-vertical">
                {[
                  { value: 'weekly', label: t('settings.notifications.progressReports.weekly'), description: t('settings.notifications.progressReports.everyWeek') },
                  { value: 'monthly', label: t('settings.notifications.progressReports.monthly'), description: t('settings.notifications.progressReports.everyMonth') },
                  { value: 'quarterly', label: t('settings.notifications.progressReports.quarterly'), description: t('settings.notifications.progressReports.fourTimesPerYear') }
                ].map((option) => (
                  <button
                    key={option.value}
                    className={`form-radio-option ${settings.notifications.progressReportFrequency === option.value ? 'form-radio-option--selected' : ''}`}
                    onClick={() => updateSetting('notifications', 'progressReportFrequency', option.value)}
                  >
                    <span className="form-radio-button">
                      {settings.notifications.progressReportFrequency === option.value && <span className="form-radio-dot" />}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                      <span style={{ fontWeight: 500 }}>{option.label}</span>
                      <span style={{ fontSize: '11px', color: 'var(--color-gray-600)' }}>{option.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* 3. Sensor Configuration */}
        <Card className="settings-card" padding="lg">
          <div className="settings-card-header">
            <div>
              <h2 className="settings-card-title">
                <SettingsIcon size={20} />
                {t('settings.sensors.title')}
              </h2>
              <p className="settings-card-description">
                {t('settings.sensors.subtitle')}
              </p>
            </div>
          </div>

          <div className="settings-form">
            {/* Update Interval */}
            <div className="form-field">
              <label className="form-label">{t('settings.sensors.updateInterval')}</label>
              <div className="form-compound-input">
                <input
                  type="number"
                  className="form-input form-input--number"
                  value={settings.sensors.updateInterval}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    // Clamp value between min and max
                    const clampedValue = Math.min(Math.max(value, 15), 60);
                    updateSetting('sensors', 'updateInterval', clampedValue);
                  }}
                  min="15"
                  max="60"
                />
                <span className="form-compound-unit">{t('settings.sensors.minutes')}</span>
              </div>
              <p className="form-helper">{t('settings.sensors.updateIntervalHelper')}</p>
            </div>

            {/* Data Retention */}
            <div className="form-field">
              <label className="form-label">{t('settings.sensors.dataRetention')}</label>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-gray-50)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-md)',
                fontSize: '13px',
                color: 'var(--color-gray-700)'
              }}>
                <span style={{ fontWeight: 500 }}>{t('settings.sensors.dataRetentionStandardLabel')}:</span>
                <span>{t('settings.sensors.dataRetentionStandardValue')}</span>
              </div>
              <p className="form-helper">{t('settings.sensors.dataRetentionHelper')}</p>
            </div>

            {/* Auto Refresh */}
            <div className="form-toggle-row">
              <div>
                <div className="form-label">{t('settings.sensors.autoRefresh')}</div>
                <p className="form-helper">{t('settings.sensors.autoRefreshHelper')}</p>
              </div>
              <Toggle
                checked={settings.sensors.autoRefresh}
                onChange={(checked) => updateSetting('sensors', 'autoRefresh', checked)}
              />
            </div>

            {/* Refresh Interval */}
            <div className="form-field">
              <label className="form-label">{t('settings.sensors.refreshInterval')}</label>
              <div className="form-compound-input">
                <input
                  type="number"
                  className="form-input form-input--number"
                  value={settings.sensors.refreshInterval}
                  onChange={(e) => updateSetting('sensors', 'refreshInterval', Number(e.target.value))}
                  min="10"
                  max="300"
                  disabled={!settings.sensors.autoRefresh}
                />
                <span className="form-compound-unit">{t('settings.sensors.seconds')}</span>
              </div>
              {!settings.sensors.autoRefresh && (
                <p className="form-helper form-helper--disabled">{t('settings.sensors.refreshIntervalDisabledHint')}</p>
              )}
            </div>
          </div>
        </Card>

        {/* 4. Display Preferences */}
        <Card className="settings-card" padding="lg">
          <div className="settings-card-header">
            <div>
              <h2 className="settings-card-title">
                <Monitor size={20} />
                {t('settings.display.title')}
              </h2>
              <p className="settings-card-description">
                {t('settings.display.subtitle')}
              </p>
            </div>
          </div>

          <div className="settings-form">
            {/* Theme */}
            <div className="form-field">
              <label className="form-label">{t('settings.display.theme')}</label>
              <div className="form-theme-grid">
                <button
                  className={`form-theme-option ${settings.display.theme === 'light' ? 'form-theme-option--selected' : ''}`}
                  onClick={() => updateSetting('display', 'theme', 'light')}
                >
                  <Sun size={32} />
                  <span>{t('settings.display.lightTheme')}</span>
                </button>
                <button
                  className={`form-theme-option ${settings.display.theme === 'dark' ? 'form-theme-option--selected' : ''}`}
                  onClick={() => updateSetting('display', 'theme', 'dark')}
                >
                  <Moon size={32} />
                  <span>{t('settings.display.darkTheme')}</span>
                </button>
              </div>
            </div>

            {/* Chart Type */}
            <div className="form-field">
              <label className="form-label">{t('settings.display.chartType')}</label>
              <div className="form-chart-grid">
                <button
                  className={`form-chart-option ${settings.display.chartType === 'line' ? 'form-chart-option--selected' : ''}`}
                  onClick={() => updateSetting('display', 'chartType', 'line')}
                >
                  <LineChart size={40} />
                  <span>{t('settings.display.lineChart')}</span>
                </button>
                <button
                  className={`form-chart-option ${settings.display.chartType === 'bar' ? 'form-chart-option--selected' : ''}`}
                  onClick={() => updateSetting('display', 'chartType', 'bar')}
                >
                  <BarChart size={40} />
                  <span>{t('settings.display.barChart')}</span>
                </button>
                <button
                  className={`form-chart-option ${settings.display.chartType === 'area' ? 'form-chart-option--selected' : ''}`}
                  onClick={() => updateSetting('display', 'chartType', 'area')}
                >
                  <TrendingUp size={40} />
                  <span>{t('settings.display.areaChart')}</span>
                </button>
              </div>
            </div>

            {/* Layout */}
            <div className="form-field">
              <label className="form-label">{t('settings.display.layout')}</label>
              <select
                className="form-select"
                value={settings.display.layout}
                onChange={(e) => updateSetting('display', 'layout', e.target.value)}
              >
                <option value="grid">{t('settings.display.gridLayout')}</option>
                <option value="list">{t('settings.display.listLayout')}</option>
                <option value="compact">{t('settings.display.compactLayout')}</option>
              </select>
            </div>

            {/* Cards Per Row */}
            <div className="form-field">
              <label className="form-label">{t('settings.display.cardsPerRow')}</label>
              <div className="form-number-stepper">
                <button
                  className="form-stepper-button"
                  onClick={() => updateSetting('display', 'cardsPerRow', Math.max(2, settings.display.cardsPerRow - 1))}
                  disabled={settings.display.cardsPerRow <= 2}
                >
                  <Minus size={16} />
                </button>
                <span className="form-stepper-value">{settings.display.cardsPerRow}</span>
                <button
                  className="form-stepper-button"
                  onClick={() => updateSetting('display', 'cardsPerRow', Math.min(4, settings.display.cardsPerRow + 1))}
                  disabled={settings.display.cardsPerRow >= 4}
                >
                  <Plus size={16} />
                </button>
              </div>
              <p className="form-helper">{t('settings.display.cardsPerRowHelper')}</p>
            </div>
          </div>
        </Card>

        {/* 5. System Information */}
        <Card className="settings-card settings-card--readonly" padding="lg">
          <div className="settings-card-header">
            <div>
              <h2 className="settings-card-title">
                <Database size={20} />
                {t('settings.systemInfo.title')}
              </h2>
              <p className="settings-card-description">
                {t('settings.systemInfo.subtitle')}
              </p>
            </div>
          </div>

          <div className="system-info-grid">
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.version')}</span>
              <span className="system-info-value">1.0.0</span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.databaseStatus')}</span>
              <span className="system-info-badge system-info-badge--success">
                <CheckCircle size={14} />
                {t('settings.systemInfo.database.connected')}
              </span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.lastBackup')}</span>
              <span className="system-info-value">
                <Clock size={14} />
                {lastBackupText}
              </span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.systemUptime')}</span>
              <span className="system-info-value">{systemUptimeText}</span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.totalSensors')}</span>
              <span className="system-info-value">24</span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.activeSensors')}</span>
              <span className="system-info-value">22</span>
            </div>
            <div className="system-info-row">
              <span className="system-info-label">{t('settings.systemInfo.labels.dataPoints')}</span>
              <span className="system-info-value">1,245,678</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <Modal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          title={t('settings.modals.reset.title')}
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <AlertCircle size={48} color="#8A7A4A" style={{ marginBottom: '16px' }} />
            <p style={{ fontSize: '14px', color: '#6B6B6B', marginBottom: '24px' }}>
              {t('settings.modals.reset.message')}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button variant="secondary" onClick={() => setShowResetModal(false)}>
                {t('settings.modals.reset.cancel')}
              </Button>
              <Button variant="danger" onClick={handleReset}>
                {t('settings.resetToDefaults')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
